import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { ModelProps, Number3 } from '@fabric-web/3d-viewer';
import * as state from '../../state';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { useAssemblyCadModelsBasePath } from './useAssemblyCadModelsBasePath';

const makeModelProps = (id: string, assemblyName: string, cadModelPath: string): ModelProps => {
  return {
    id,
    name: assemblyName,
    url: getCloudfrontUrl(cadModelPath),
    material: {
      color: 0xffffff,
      transparent: true,
      opacity: 0.5,
    },
    enableClippingByVisibilityBox: false,
  };
};

type Props = {
  onModelClick?: (point: Number3) => void;
};

export const useAssemblyCadModels = ({ onModelClick }: Props): ModelProps[] | undefined => {
  const showAssemblyCadModel = useAtomValue(state.showAssemblyCadModel);
  const selectedAssemblyDetails = useAtomValue(state.selectedAssemblyDetails);

  const { assemblyCadModelsBasePath: basePath } = useAssemblyCadModelsBasePath();

  const models: ModelProps[] | undefined = useMemo(() => {
    if (!showAssemblyCadModel) return undefined;
    if (!basePath) return undefined;
    const cadModelPath = selectedAssemblyDetails?.cadModelPath;
    if (!cadModelPath) return undefined;

    const separator = basePath.endsWith('/') ? '' : '/';
    const fullCadModelPath = `${basePath}${separator}${cadModelPath}`;

    return [
      {
        ...makeModelProps(
          selectedAssemblyDetails.id,
          selectedAssemblyDetails.tagName,
          fullCadModelPath
        ),
        onClick: onModelClick,
      },
    ];
  }, [showAssemblyCadModel, selectedAssemblyDetails, basePath, onModelClick]);

  return models;
};
